import type {
  ChapterPlan,
  DramaticBeatFunction,
  DramaticChapterBeat,
  FourBeatChapterContract,
  Ledger,
} from '../types';
import {
  ALLOWED_EXPANSION_METHODS,
  FORBIDDEN_PADDING_METHODS,
  resolveChapterWordTargets,
} from './chapterExpansion.service';
import {
  buildAtomicEventDistributionDiagnostic,
  extractAtomicChapterEvents,
  formatChapterEndingState,
  groupAtomicEventsByBeat,
  normalizeEndingTurnText,
} from './fourBeatAtomicEvents.service';

export const FOUR_BEAT_FUNCTIONS: DramaticBeatFunction[] = [
  'OPENING_PRESSURE',
  'ESCALATION',
  'DECISION_REVERSAL',
  'CONSEQUENCE_HOOK',
];

const BASE_BEAT_MINS = [600, 625, 700, 575] as const;
export const FOUR_BEAT_BASE_MINS = BASE_BEAT_MINS;
const BASE_BEAT_MAXS = [700, 750, 850, 700] as const;

const CONFRONTATION_MARKERS =
  /\b(argue|fight|confront|threat|attack|betray|refuse|oppose|danger|chase|escape|discover|reveal)\b/i;
const DIALOGUE_MARKERS = /\b(dialogue|argue|discuss|confront|negotiate|plead|shout|whisper)\b/i;
const ACTION_MARKERS = /\b(run|fight|escape|attack|chase|climb|cross|restore|repair|build)\b/i;

function beatFunctionForSequence(sequence: 1 | 2 | 3 | 4): DramaticBeatFunction {
  return FOUR_BEAT_FUNCTIONS[sequence - 1];
}

function beatIdForSequence(sequence: number): string {
  return `beat-${sequence}`;
}

function deriveOpeningState(plan: ChapterPlan | undefined, prevSummary?: string): string {
  if (plan?.continuityNotes?.length) return plan.continuityNotes[0];
  if (prevSummary?.trim()) return `Continuing from: ${prevSummary.slice(0, 180)}`;
  return plan?.summary || 'POV character enters the chapter under active pressure.';
}

function deriveChapterEndingState(plan: ChapterPlan | undefined): string {
  const beats = plan?.requiredBeats || [];
  if (beats.length > 0) {
    return formatChapterEndingState(beats[beats.length - 1]);
  }
  if (plan?.summary?.trim()) {
    const sentences = plan.summary.split(/(?<=[.!?])\s+/).filter(Boolean);
    const ending = sentences[sentences.length - 1] || plan.summary;
    return formatChapterEndingState(ending);
  }
  return 'Chapter ends with changed stakes and a forward hook.';
}

function deriveLocation(plan: ChapterPlan | undefined, sequence: number): string {
  const locations = plan?.locationFocus || [];
  if (locations.length === 0) return 'Current chapter location from ledger/plan';
  if (locations.length === 1) return locations[0];
  return locations[Math.min(sequence - 1, locations.length - 1)];
}

function scoreBeatWeight(text: string): number {
  let weight = 1;
  if (CONFRONTATION_MARKERS.test(text)) weight += 0.35;
  if (DIALOGUE_MARKERS.test(text)) weight += 0.2;
  if (ACTION_MARKERS.test(text)) weight += 0.2;
  if (/\b(decision|choose|reveal|discover|consequence|fail|betray)\b/i.test(text)) weight += 0.15;
  return weight;
}

/** @deprecated Use atomic event distribution via extractAtomicChapterEvents. */
export function distributeRoadmapBeats(requiredBeats: string[]): string[][] {
  const buckets: string[][] = [[], [], [], []];
  if (requiredBeats.length === 0) return buckets;
  requiredBeats.forEach((beat, index) => {
    buckets[index % 4].push(beat);
  });
  return buckets;
}

export function allocateBeatWordBudgets(
  beatTexts: string[],
  preferredTargetWords: number,
): Array<{ targetMinWords: number; targetMaxWords: number }> {
  const weights = beatTexts.map((text, index) => {
    const base = [1.0, 1.05, 1.2, 1.0][index] ?? 1;
    return base * scoreBeatWeight(text || '');
  });
  const totalWeight = weights.reduce((sum, w) => sum + w, 0) || 1;

  const mins = weights.map((weight, index) =>
    Math.max(
      BASE_BEAT_MINS[index] ?? 550,
      Math.round((preferredTargetWords * weight) / totalWeight * 0.92),
    ),
  );

  const maxs = mins.map((min, index) =>
    Math.max(min + 80, BASE_BEAT_MAXS[index] ?? min + 120),
  );

  let sumMins = mins.reduce((sum, n) => sum + n, 0);
  if (sumMins < preferredTargetWords) {
    const deficit = preferredTargetWords - sumMins;
    const boostOrder = [2, 1, 0, 3];
    for (let i = 0; i < deficit; i += 1) {
      const idx = boostOrder[i % boostOrder.length];
      mins[idx] += 1;
      maxs[idx] += 1;
    }
  }

  return mins.map((targetMinWords, index) => ({
    targetMinWords,
    targetMaxWords: maxs[index],
  }));
}

function buildBeatSpec(params: {
  sequence: 1 | 2 | 3 | 4;
  plan?: ChapterPlan;
  primaryEvents: { eventId: string; description: string }[];
  budgets: { targetMinWords: number; targetMaxWords: number };
  previousEndingState?: string;
  chapterEndingState: string;
}): DramaticChapterBeat {
  const { sequence, plan, primaryEvents, budgets, previousEndingState, chapterEndingState } = params;
  const fn = beatFunctionForSequence(sequence);
  const pov = plan?.pov || plan?.characterFocus?.[0] || 'POV character';
  const location = deriveLocation(plan, sequence);
  const primaryDescriptions = primaryEvents.map((event) => event.description);
  const primaryEvent = primaryDescriptions[0] || plan?.summary || 'Advance the chapter conflict';
  const acceptanceFloorWords = Math.max(
    300,
    Math.floor((BASE_BEAT_MINS[sequence - 1] ?? 550) * 0.65),
  );

  const templates: Record<DramaticBeatFunction, Omit<DramaticChapterBeat, 'beatId' | 'sequence' | 'targetMinWords' | 'targetMaxWords' | 'acceptanceFloorWords' | 'primaryEventIds'>> = {
    OPENING_PRESSURE: {
      function: 'OPENING_PRESSURE',
      location,
      openingState: previousEndingState || deriveOpeningState(plan),
      dramaticGoal: `Establish immediate pressure for ${pov} and dramatize the chapter problem.`,
      obstacle: 'An unavoidable complication, demand, or discovery blocks easy progress.',
      conflict: `Active tension around: ${primaryEvent}`,
      requiredEvents: primaryDescriptions.length ? primaryDescriptions : [primaryEvent],
      dialogueOpportunity: 'Interpersonal friction or urgent exchange that clarifies stakes.',
      characterDecision: 'POV may respond immediately; a major consequential decision is optional.',
      immediateConsequence: 'The situation becomes harder, narrower, or more dangerous.',
      endingState: 'Pressure escalates into a problem that cannot be ignored.',
      transitionToNextBeat: 'The new complication forces a response that deepens conflict.',
      continuityInputs: plan?.continuityNotes || [],
      forbiddenPadding: [...FORBIDDEN_PADDING_METHODS],
    },
    ESCALATION: {
      function: 'ESCALATION',
      location,
      openingState: 'Carry forward the prior beat ending state without re-introducing the chapter.',
      dramaticGoal: 'Intensify resistance and raise personal or external cost.',
      obstacle: 'Opposition, friction, or environment actively works against the POV goal.',
      conflict: `Escalate conflict around: ${primaryDescriptions.join('; ') || primaryEvent}`,
      requiredEvents: primaryDescriptions.length ? primaryDescriptions : ['Complication that worsens available options'],
      dialogueOpportunity: 'Opposing goals collide in dialogue or negotiation under pressure.',
      characterDecision: 'POV may choose a riskier course; a major consequential decision is optional.',
      immediateConsequence: 'Cost increases and options narrow.',
      endingState: 'The chapter problem is materially worse than at opening.',
      transitionToNextBeat: 'Raised stakes force a decisive turn.',
      continuityInputs: plan?.continuityNotes || [],
      forbiddenPadding: [...FORBIDDEN_PADDING_METHODS],
    },
    DECISION_REVERSAL: {
      function: 'DECISION_REVERSAL',
      location,
      openingState: 'Enter at the moment the decisive turn becomes unavoidable.',
      dramaticGoal: 'Force the strongest confrontation, discovery, or reversal of the chapter.',
      obstacle: 'A direct challenge that cannot be deferred without losing the chapter arc.',
      conflict: `Decisive turn for: ${primaryDescriptions.join('; ') || primaryEvent}`,
      requiredEvents: primaryDescriptions.length ? primaryDescriptions : ['Irreversible decision or revelation'],
      dialogueOpportunity: 'Confrontation, confession, or argument with visible cost.',
      characterDecision: 'POV chooses, acts, reveals, confronts, or fails on-page.',
      immediateConsequence: 'The decision changes relationships, danger, or available options.',
      endingState: 'A consequential turn has occurred and cannot be undone.',
      transitionToNextBeat: 'The consequence must be dramatized, not summarized.',
      continuityInputs: plan?.continuityNotes || [],
      forbiddenPadding: [...FORBIDDEN_PADDING_METHODS],
    },
    CONSEQUENCE_HOOK: {
      function: 'CONSEQUENCE_HOOK',
      location,
      openingState: 'Open on the immediate aftermath of the decisive turn.',
      dramaticGoal: 'Dramatize results and land the chapter on forward movement.',
      obstacle: 'New complication or cost emerging from the prior decision.',
      conflict: `Consequence and hook for: ${primaryDescriptions.join('; ') || normalizeEndingTurnText(chapterEndingState)}`,
      requiredEvents: primaryDescriptions.length
        ? primaryDescriptions
        : [normalizeEndingTurnText(chapterEndingState)],
      dialogueOpportunity: 'Reaction, reckoning, or urgent planning under new facts.',
      characterDecision: 'POV accepts a new reality; a separate decision is optional if Beat 3 already decided.',
      immediateConsequence: 'World state, relationships, or danger profile updates on-page.',
      endingState: chapterEndingState,
      transitionToNextBeat: 'End with meaningful forward movement, not a roadmap recap.',
      continuityInputs: plan?.continuityNotes || [],
      forbiddenPadding: [...FORBIDDEN_PADDING_METHODS],
    },
  };

  const spec = templates[fn];
  return {
    beatId: beatIdForSequence(sequence),
    sequence,
    ...spec,
    primaryEventIds: primaryEvents.map((event) => event.eventId),
    targetMinWords: budgets.targetMinWords,
    targetMaxWords: budgets.targetMaxWords,
    acceptanceFloorWords,
    continuityInputs: [
      ...spec.continuityInputs,
      ...ALLOWED_EXPANSION_METHODS.slice(0, 3),
    ],
  };
}

export function buildFourBeatChapterContract(params: {
  plan?: ChapterPlan;
  chapterNumber: number;
  chapterLength?: string;
  ledger?: Ledger;
  prevSummary?: string;
}): FourBeatChapterContract {
  const { plan, chapterNumber, chapterLength, prevSummary } = params;
  const targets = resolveChapterWordTargets(chapterLength);
  const preferredTargetRangeMin =
    chapterLength === 'EXTENDED' ? 4000 : chapterLength === 'MICRO' ? 1000 : 2500;
  const preferredTargetRangeMax =
    chapterLength === 'EXTENDED' ? 5000 : chapterLength === 'MICRO' ? 1200 : 3000;
  const preferredTargetWords = preferredTargetRangeMin;
  const atomicEvents = extractAtomicChapterEvents(plan);
  const eventsByBeat = groupAtomicEventsByBeat(atomicEvents);
  const requiredRoadmapBeats = atomicEvents.map((event) => event.description);
  const chapterEndingState = deriveChapterEndingState(plan);

  const budgets = allocateBeatWordBudgets(
    ([1, 2, 3, 4] as const).map((sequence) =>
      (eventsByBeat.get(`beat-${sequence}`) || []).map((event) => event.description).join(' '),
    ),
    preferredTargetWords,
  );

  let previousEnding: string | undefined;
  const beats = ([1, 2, 3, 4] as const).map((sequence) => {
    const beatEvents = eventsByBeat.get(`beat-${sequence}`) || [];
    const beat = buildBeatSpec({
      sequence,
      plan,
      primaryEvents: beatEvents.map((event) => ({
        eventId: event.eventId,
        description: event.description,
      })),
      budgets: budgets[sequence - 1],
      previousEndingState: previousEnding,
      chapterEndingState,
    });
    previousEnding = beat.endingState;
    return beat;
  }) as FourBeatChapterContract['beats'];

  const forbiddenEvents = [
    ...(plan?.forbiddenMoves || []),
    'Do not repeat resolved plot points as fresh discoveries.',
    'Do not re-introduce characters already present as if newly arrived.',
  ];

  return {
    chapterNumber,
    chapterTitle: plan?.chapterTitle || plan?.title || `Chapter ${chapterNumber}`,
    minimumAcceptedWords: targets.minimumAcceptedWords,
    preferredTargetWords,
    preferredTargetRangeMin,
    preferredTargetRangeMax,
    povCharacter: plan?.pov || plan?.characterFocus?.[0] || 'POV character',
    chapterOpeningState: deriveOpeningState(plan, prevSummary),
    chapterEndingState,
    requiredRoadmapBeats,
    atomicEvents,
    continuityConstraints: plan?.continuityNotes || [],
    forbiddenEvents,
    beats,
  };
}

export function sumBeatTargetMinimums(contract: FourBeatChapterContract): number {
  return contract.beats.reduce((sum, beat) => sum + beat.targetMinWords, 0);
}

export function buildFourBeatContractPrompt(contract: FourBeatChapterContract): string {
  const beatBlocks = contract.beats
    .map((beat) => `
BEAT ${beat.sequence} (${beat.function}) — ${beat.beatId}
- Location: ${beat.location}
- Opening state: ${beat.openingState}
- Dramatic goal: ${beat.dramaticGoal}
- Obstacle: ${beat.obstacle}
- Conflict: ${beat.conflict}
- Primary atomic event IDs: ${beat.primaryEventIds.join(', ') || 'none'}
- Required events: ${beat.requiredEvents.join('; ') || 'none'}
- Dialogue opportunity: ${beat.dialogueOpportunity}
- Decision guidance: ${beat.characterDecision}
- Required consequence: ${beat.immediateConsequence}
- Ending state: ${beat.endingState}
- Transition to next beat: ${beat.transitionToNextBeat}
- Target words: ${beat.targetMinWords}–${beat.targetMaxWords}
- Structural acceptance floor: ${beat.acceptanceFloorWords}
- Continuity inputs: ${beat.continuityInputs.join('; ') || 'none'}
- Forbidden padding: ${beat.forbiddenPadding.join('; ')}
`.trim())
    .join('\n\n');

  const atomicBlock = contract.atomicEvents.length
    ? contract.atomicEvents
        .map(
          (event) =>
            `- ${event.eventId} [${event.source}] primary=${event.primaryBeatId}: ${event.description}`,
        )
        .join('\n')
    : 'none';

  return `
FOUR-BEAT CHAPTER CONTRACT (AUTHORITATIVE):
- Chapter: ${contract.chapterNumber} — ${contract.chapterTitle}
- POV: ${contract.povCharacter}
- Hard minimum: ${contract.minimumAcceptedWords} words
- Preferred target: ${contract.preferredTargetRangeMin}–${contract.preferredTargetRangeMax} words
- Chapter opening state: ${contract.chapterOpeningState}
- Chapter ending state: ${contract.chapterEndingState}
- Atomic events (each assigned to one primary beat; report satisfied IDs in requiredEventsSatisfied):
${atomicBlock}
- Continuity constraints: ${contract.continuityConstraints.join('; ') || 'none'}
- Forbidden events: ${contract.forbiddenEvents.join('; ') || 'none'}

${beatBlocks}

DRAMATIZATION REQUIREMENTS:
- Write exactly four developed beats in the beats array.
- Each beat is an active scene, not a summary.
- Use physical action, dialogue, reaction, and consequence.
- Do NOT compress a beat into one paragraph.
- Do NOT rush the fourth beat.
- Do NOT end because roadmap facts were mentioned — dramatize them.
- Populate requiredEventsSatisfied with atomic event IDs (e.g. event-1), not full paragraphs.
- Maintain POV and character knowledge continuity across beats.
- Distinguish detailed dramatization from padding, repetition, or procedural filler.
- "Do not pad" does NOT mean "be concise" — aim for per-beat word targets through story substance.
- Sum of beat minimum targets: ${sumBeatTargetMinimums(contract)} words (preferred chapter target ${contract.preferredTargetWords}+).
`.trim();
}

export function enrichPlanWithFourBeatContract(
  plan: ChapterPlan,
  chapterNumber: number,
  chapterLength?: string,
): { plan: ChapterPlan; contract: FourBeatChapterContract } {
  const contract = buildFourBeatChapterContract({ plan, chapterNumber, chapterLength });
  const distributed = contract.atomicEvents.map((event) => event.description);
  const mergedBeats = [...new Set([...(plan.requiredBeats || []), ...distributed.filter(Boolean)])];

  return {
    plan: {
      ...plan,
      requiredBeats: mergedBeats,
      continuityNotes: [
        ...(plan.continuityNotes || []),
        `Four-beat contract prepared: ${contract.beats.map((b) => b.function).join(' → ')}.`,
      ],
    },
    contract,
  };
}

export { buildAtomicEventDistributionDiagnostic };
